import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./admin-content.server";


export interface FaqRow {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
  published: boolean;
}

export interface AnnouncementValue {
  text: string;
  type: "info" | "warning" | "success";
  active: boolean;
}

export interface LandingCopyValue {
  hero_eyebrow?: string;
  hero_title?: string;
  hero_subtitle?: string;
  cta_label?: string;
}

// ---------- Public reads (no admin gate) ----------

export const listPublishedFaqs = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("faqs")
    .select("id, question, answer, sort_order, published")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  return { faqs: (data ?? []) as FaqRow[] };
});

export const getPublicContent = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("key, value")
    .in("key", ["announcement", "landing_copy"]);
  const map = new Map<string, unknown>();
  (data ?? []).forEach((r) => map.set(r.key, r.value));
  return {
    announcement: (map.get("announcement") as AnnouncementValue | undefined) ?? null,
    landing: (map.get("landing_copy") as LandingCopyValue | undefined) ?? null,
  };
});

// ---------- Admin: FAQs ----------

export const adminListFaqs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("faqs")
      .select("id, question, answer, sort_order, published")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return { faqs: (data ?? []) as FaqRow[] };
  });

export const adminUpsertFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        id: z.string().uuid().optional(),
        question: z.string().min(1).max(300),
        answer: z.string().min(1).max(5000),
        sort_order: z.number().int().min(0).max(999),
        published: z.boolean(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("faqs")
        .update({
          question: data.question,
          answer: data.answer,
          sort_order: data.sort_order,
          published: data.published,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin.from("faqs").insert({
        question: data.question,
        answer: data.answer,
        sort_order: data.sort_order,
        published: data.published,
      });
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const adminDeleteFaq = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("faqs").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Admin: Announcement + landing copy ----------

export const adminGetContentSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("key, value")
      .in("key", ["announcement", "landing_copy"]);
    const map = new Map<string, unknown>();
    (data ?? []).forEach((r) => map.set(r.key, r.value));
    return {
      announcement: (map.get("announcement") as AnnouncementValue | undefined) ?? {
        text: "",
        type: "info" as const,
        active: false,
      },
      landing: (map.get("landing_copy") as LandingCopyValue | undefined) ?? {},
    };
  });

export const adminSaveAnnouncement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        text: z.string().max(500),
        type: z.enum(["info", "warning", "success"]),
        active: z.boolean(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("site_settings").upsert({
      key: "announcement",
      value: data as never,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminSaveLandingCopy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        hero_eyebrow: z.string().max(120).optional(),
        hero_title: z.string().max(200).optional(),
        hero_subtitle: z.string().max(400).optional(),
        cta_label: z.string().max(60).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("site_settings").upsert({
      key: "landing_copy",
      value: data as never,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
